import type { TFunction } from 'i18next';
import { formatDate } from './formatDate';
import type { AvailabilityState, PeriodState } from '../api/unavailabilitiesApi';

/**
 * How an absence reads on screen — the one place, for the mobile screen and the four backoffice
 * spots alike. The states themselves are decided by Postgres (see `UserUnavailability`); nothing
 * here compares a date, it only puts words and separators around what the API sent.
 */

/**
 * A `YYYY-MM-DD` day, read as a local day. `new Date('2026-09-01')` is **UTC** midnight, which a
 * browser west of Greenwich renders as the 31st of August — the very one-day drift the `DATE`
 * column exists to avoid, re-introduced on the last mile.
 */
const formatDay = (isoDay: string): string => formatDate(`${isoDay}T00:00:00`);

/** `01/09/2026 → 15/09/2026`, the arrow both mock-ups use. Rendered in Geist Mono by its callers. */
export const formatPeriod = (startDate: string, endDate: string): string =>
  `${formatDay(startDate)} → ${formatDay(endDate)}`;

/**
 * A `switch` of string literals, never a key built by concatenating the state onto a prefix: the
 * repo's static guard requires a literal, and a concatenated key is invisible to the parity check.
 */
export const periodStateLabel = (t: TFunction, state: PeriodState): string => {
  switch (state) {
    case 'current':
      return t('Ongoing');
    case 'upcoming':
      return t('Upcoming');
    default:
      return t('Ended');
  }
};

/**
 * The technician's derived state. `upcoming` is **not** a third state of availability — he is
 * available today — which is why its wording says "soon" rather than plainly "unavailable".
 */
export const availabilityLabel = (t: TFunction, state: AvailabilityState): string => {
  switch (state) {
    case 'unavailable':
      return t('Unavailable');
    case 'upcoming':
      return t('Unavailable soon');
    default:
      return t('Available');
  }
};
